/**
 * Showing only the lines that still need a decision.
 *
 * On a seventy-line enquiry the four with no supplier are invisible: scattered through the table
 * in the customer's own order, findable only by scrolling for an empty cell. The column-head
 * control narrows the table to exactly those, and the numbering still reads upward because the
 * enquiry's own order is kept.
 *
 * ---- What "undecided" means ----
 *
 * Not "empty". Every line carries a route from extraction, so absence is not the test; what makes
 * a line unresolved is having nothing behind it — an RFQ line with no supplier to ask, or a
 * contract line whose blanket order carries no rate for it (which means the contract does not
 * actually cover the item, and `contractQuotation.ts` refuses to price it).
 */

import { isRfqLine } from './rfq'
import type { EnquiryItem } from './types'

/** The two things a purchaser resolves on this screen. */
export type TriageFilter = 'supplier' | 'route'

/**
 * An RFQ line with nobody to ask.
 *
 * A CONTRACT line is excluded deliberately: it is priced by its blanket order and never goes out
 * for a quote, so it is not waiting for a supplier. Counting it would bury the lines that are.
 */
export function needsSupplier(line: EnquiryItem): boolean {
  return isRfqLine(line) && line.suppliers.length === 0
}

/**
 * A line with nothing behind it either way — no supplier to quote it and no contract rate.
 *
 * A contract line with `selected_rate: 0` counts: the contract was selected but does not cover
 * this item, which is exactly the state that reaches the customer quotation at 0.00 if nobody
 * notices.
 */
export function needsRoute(line: EnquiryItem): boolean {
  if (isRfqLine(line)) return line.suppliers.length === 0
  return (line.selected_rate ?? 0) <= 0
}

/**
 * Only the lines still needing a decision. `null` returns them all, untouched.
 *
 * FILTERS rather than sorts (Akhil, 2026-09-09). Floating them to the top was tried first and is
 * not what the question asks: "which lines still have no supplier?" wants those lines, and on a
 * seventy-line enquiry the answer is still buried in seventy rows if the other sixty-six are left
 * on screen underneath.
 *
 * The enquiry's own order is preserved within the result, so the customer's numbering still reads
 * upward — line 12 is still after line 3 — and one more click restores the full list.
 */
export function filterForTriage(lines: readonly EnquiryItem[], filter: TriageFilter | null): EnquiryItem[] {
  if (filter === null) return [...lines]
  return lines.filter(filter === 'supplier' ? needsSupplier : needsRoute)
}

/** How many lines the filter would show — on the control, so it says what it will do. */
export function countUndecided(lines: readonly EnquiryItem[], filter: TriageFilter): number {
  return lines.filter(filter === 'supplier' ? needsSupplier : needsRoute).length
}
